import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/product/ProductCard";
import Marquee from "../components/Marquee";
import CrossfadeBanner from "../components/CrossfadeBanner";
import HeroMedia from "../components/HeroMedia";
import { HERO_IMAGE, ABOUT_MORPH_IMAGES } from "../data/products";
import { useStore } from "../context/StoreContext";

const TICKER = [
  "DAMAGE CULTURE",
  "DAMAGING THE CULTURE",
  "EST. 2025",
  "ONE OF ONES",
  "UPCYCLED IN INDIA",
];

const HomePage = () => {
  const { products } = useStore();
  const [morphIdx, setMorphIdx] = useState(0);

  const FEATURED = useMemo(() => {
    const staples = products.filter((p) => p.category === "STAPLE").slice(0, 3);
    const excl = products.filter((p) => p.category === "EXCLUSIVE").slice(0, 3);
    const out = [];
    for (let i = 0; i < 3; i++) {
      if (staples[i]) out.push(staples[i]);
      if (excl[i]) out.push(excl[i]);
    }
    return out;
  }, [products]);

  const BANNER_IMAGES = useMemo(() => {
    const exclImgs = products.filter((p) => p.category === "EXCLUSIVE").flatMap((p) => p.images).slice(0, 5);
    const upImgs = products.filter((p) => (p.tags || []).includes("upcycled")).flatMap((p) => p.images).slice(0, 5);
    const customImgs = products.flatMap((p) => p.images).slice(0, 5);
    return { exclusives: exclImgs, upcycled: upImgs, custom: customImgs };
  }, [products]);

  useEffect(() => {
    const id = setInterval(
      () => setMorphIdx((i) => (i + 1) % ABOUT_MORPH_IMAGES.length),
      1000
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="page-home">
      {/* A. HERO */}
      <section className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-charcoal">
        <HeroMedia src={HERO_IMAGE} alt="Damage Culture" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/10 to-ink/80 z-[1]" />

        {/* M rating chip */}
        <div className="absolute top-[90px] left-4 md:left-10 flex items-center gap-3 z-10">
          <span className="w-10 h-10 border-2 border-cream flex items-center justify-center font-display text-2xl text-cream">
            M
          </span>
          <div className="font-mono text-[10px] uppercase tracking-widest text-cream leading-tight">
            <p>MATURE 17+</p>
            <p className="opacity-70">CONTENT RATED BY USMF</p>
          </div>
        </div>

        {/* center bottom CTA */}
        <div className="absolute bottom-24 left-0 right-0 flex flex-col items-center text-center px-4 z-10">
          <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-cream/80 mb-4">
            EST. 2025 / ONE-OF-ONES / UPCYCLED
          </p>
          <Link
            to="/shop"
            data-testid="hero-shop-all"
            className="font-wordmark uppercase text-6xl sm:text-7xl md:text-8xl text-cream leading-none tracking-tight hover:text-dc-red transition-colors"
          >
            Shop All
          </Link>
        </div>

        {/* bottom ticker */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <Marquee items={TICKER} variant="red" />
        </div>
      </section>

      {/* B. STAPLES | EXCLUSIVES split */}
      <section className="grid grid-cols-2 gap-px bg-ink/10">
        <Link
          to="/shop?category=staples"
          data-testid="split-staples"
          className="group flex flex-col items-center justify-center text-center px-3 sm:px-8 md:px-12 py-14 sm:py-20 md:py-24 bg-cream hover:bg-cream-soft transition-colors"
        >
          <h2
            className="font-display uppercase leading-[0.95] tracking-tight text-dc-red transition-colors group-hover:opacity-80"
            style={{ fontSize: "clamp(22px, 6.4vw, 70px)" }}
          >
            Staples
          </h2>
          <p className="font-mono text-[10px] md:text-xs font-bold uppercase tracking-widest mt-2 text-ink/70">
            SHOP STAPLES &rarr;
          </p>
        </Link>
        <Link
          to="/exclusives"
          data-testid="split-exclusives"
          className="group flex flex-col items-center justify-center text-center px-3 sm:px-8 md:px-12 py-14 sm:py-20 md:py-24 bg-cream hover:bg-cream-soft transition-colors"
        >
          <h2
            className="font-display uppercase leading-[0.95] tracking-tight text-ink transition-colors group-hover:text-dc-red"
            style={{ fontSize: "clamp(22px, 6.4vw, 70px)" }}
          >
            Exclusives
          </h2>
          <p className="font-mono text-[10px] md:text-xs font-bold uppercase tracking-widest mt-2 text-ink/70">
            SHOP 1 OF 1S &rarr;
          </p>
        </Link>
      </section>

      {/* C. Featured store */}
      <section className="px-4 md:px-12 py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-8 md:mb-12">
            <h2 className="font-display uppercase text-3xl sm:text-5xl md:text-6xl leading-none">
              Featured / 06
            </h2>
            <p className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-ink/70">
              ONE TAP TO SHOP
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {FEATURED.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/shop"
              data-testid="home-explore-more"
              className="btn-ghost"
            >
              Explore More &rarr;
            </Link>
            <p className="font-mono text-[10px] md:text-xs uppercase tracking-widest mt-4 text-ink/60">
              SEE EVERYTHING &darr;
            </p>
          </div>
        </div>
      </section>

      {/* D. Three auto-swiping banners */}
      <section className="px-4 md:px-12 pb-14 md:pb-20">
        <div className="max-w-[1440px] mx-auto space-y-4 md:space-y-6">
          {[
            { name: "SHOP EXCLUSIVES", to: "/exclusives", imgs: BANNER_IMAGES.exclusives, idx: "01 / 03" },
            { name: "SHOP UPCYCLED", to: "/shop?tag=upcycled", imgs: BANNER_IMAGES.upcycled, idx: "02 / 03" },
            { name: "CUSTOMIZE", to: "/shop?tag=custom", imgs: BANNER_IMAGES.custom, idx: "03 / 03" },
          ].map((b) => (
            <Link
              key={b.name}
              to={b.to}
              data-testid={`home-banner-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="block group"
            >
              <CrossfadeBanner
                images={b.imgs}
                interval={1000}
                className="h-[320px] md:h-[420px]"
              >
                <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-widest text-cream/70">
                  {b.idx}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8 flex items-end justify-between gap-4">
                  <h3 className="font-display uppercase text-cream text-3xl sm:text-5xl md:text-6xl leading-none">
                    {b.name}
                  </h3>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-cream whitespace-nowrap">
                    DISCOVER &rarr;
                  </p>
                </div>
              </CrossfadeBanner>
            </Link>
          ))}
        </div>
      </section>

      {/* E. About teaser */}
      <section className="bg-cream-soft border-t border-ink/12 px-4 md:px-12 py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-center gap-4 mb-10">
            <h2 className="font-display uppercase text-3xl md:text-5xl leading-none">
              About Us
            </h2>
            <span className="flex-1 h-px bg-ink/30" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-10 md:gap-16 items-start">
            <div>
              <p className="font-body text-xl md:text-2xl leading-relaxed text-ink/90">
                "Damage culture" can be understood as a shift in attitude where breaking rules, aesthetics, or expectations is seen as a form of expression. It shows up in how people dress, create, consume, or communicate. Embracing imperfection is a style.
              </p>
              <Link
                to="/about"
                data-testid="home-about-readmore"
                className="inline-block mt-6 font-mono text-xs uppercase tracking-widest text-ink hover:text-dc-red transition-colors"
              >
                READ MORE &rarr;
              </Link>
            </div>
            <div
              className="relative w-[160px] h-[200px] md:w-[300px] md:h-[380px] rounded-[24px] shadow-card overflow-hidden mx-auto md:mx-0"
              aria-hidden="true"
            >
              {ABOUT_MORPH_IMAGES.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: i === morphIdx ? 1 : 0 }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
